/**
 * Client inbox — `/client-reports/inbox`. Scoped to the GLOBAL active brand
 * (switched from the bottom-left sidebar button), like every sibling tab.
 *
 * Surfaces the incoming client inputs raised from the white-label report portal
 * — questions and next-month requests — for the active brand. Each thread shows
 * the body, type, status, timestamp and the reply thread, with a reply box
 * (POST reply → status 'answered', emails the client) and a Resolve action
 * (POST resolve → status 'resolved'). Gated on `portal.clients.manage`
 * (owner/manager/team_lead) both here (via ClientReportsPage) and in the sidebar
 * sub-nav.
 *
 * DEV-only inert gate: the client-inputs API ships on the DEV-first portal
 * rollout (central-api branch feat/report-phase3-client-inputs) while
 * tara-client auto-deploys — so outside DEV the tab could reach the page before
 * the API exists. `INBOX_ENABLED` keeps the fetch + actions inert (a friendly
 * "coming soon" note) in non-DEV, mirroring ManageClientsPanel's ROSTER_ENABLED.
 * Flip it alongside the route.ts portal flags when the API is promoted.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import { HOME_ROUTE } from '../../../lib/constants/route';
import { setMessage } from '../../../store/reducers/appSlice';
import type { RootState } from '../../../store/store';
import {
  listClientInputs,
  replyToClientInput,
  resolveClientInput,
  type ClientInput,
  type ClientInputStatus,
} from '../../../lib/api/client-reports/client-inputs-api';
import ClientReportsNav from '../ClientReportsNav';
import { englishT, type Translate } from '../agents/agent-helpers';
import { formatDateTime } from '../workbench/workbench-labels';
import '../client-reports.scss';

// The client-inputs API shipped to staging + live in the 2026-07-23 release,
// so the DEV-first hold (see route.ts portal flags) is over.
const INBOX_ENABLED = true;

const REPLY_MAX = 4000;

type StatusFilter = 'all' | ClientInputStatus;

const STATUS_TABS: StatusFilter[] = ['open', 'answered', 'resolved', 'all'];

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  answered: 'Answered',
  resolved: 'Resolved',
};

const TYPE_LABEL: Record<string, string> = {
  question: 'Question',
  next_month_request: 'Next-month request',
};

/** The empty state per status filter, a full sentence so it translates whole. */
const EMPTY_LABEL: Record<StatusFilter, string> = {
  all: 'No client inputs for this brand yet.',
  open: 'No open client inputs for this brand.',
  answered: 'No answered client inputs for this brand.',
  resolved: 'No resolved client inputs for this brand.',
};

/** A thread's status; one this page does not know is shown as sent. */
export const clientInputStatusLabel = (
  status: string,
  t: Translate = englishT
): string =>
  STATUS_LABEL[status]
    ? t(`ClientInboxStatus_${status}`, STATUS_LABEL[status])
    : status;

/** A thread's type; one this page does not know is shown as sent. */
export const clientInputTypeLabel = (
  type: string,
  t: Translate = englishT
): string =>
  TYPE_LABEL[type] ? t(`ClientInboxType_${type}`, TYPE_LABEL[type]) : type;

/** A timestamp in the app language's date and time format; a dash without one. */
const formatWhen = (iso: string | null, language?: string): string =>
  formatDateTime(iso, language) || '—';

const authorLabel = (authorType: string, t: Translate): string =>
  authorType === 'client'
    ? t('ClientInboxAuthorClient', 'Client')
    : t('ClientInboxAuthorYou', 'You');

interface ThreadCardProps {
  input: ClientInput;
  busy: boolean;
  onReply: (id: string, body: string) => Promise<void>;
  onResolve: (id: string) => Promise<void>;
}

const ThreadCard = ({ input, busy, onReply, onResolve }: ThreadCardProps) => {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState('');
  const resolved = input.status === 'resolved';

  const submitReply = async () => {
    const body = draft.trim();
    if (!body) return;
    await onReply(input.id, body);
    setDraft('');
  };

  return (
    <article className='client-inbox__thread'>
      <header className='client-inbox__thread-head'>
        <div className='client-inbox__thread-headings'>
          <div className='client-inbox__thread-tags'>
            <span className='client-inbox__type'>
              {clientInputTypeLabel(input.type, t)}
            </span>
            <span
              className={`client-reports__tag client-inbox__status client-inbox__status--${input.status}`}
            >
              {clientInputStatusLabel(input.status, t)}
            </span>
          </div>
          <div className='client-inbox__thread-meta'>
            <span className='client-inbox__client'>
              {input.client?.name ||
                input.client?.email ||
                t('ClientInboxAuthorClient', 'Client')}
            </span>
            {input.client?.name && input.client?.email ? (
              <span className='client-inbox__client-email'>
                {input.client.email}
              </span>
            ) : null}
            <span className='client-inbox__when'>
              {formatWhen(input.created_at, i18n.language)}
            </span>
          </div>
        </div>
      </header>

      {input.context ? (
        <div className='client-inbox__context'>
          {t('ClientInboxContext', 'Re: {{context}}', {
            context: input.context,
          })}
        </div>
      ) : null}

      <p className='client-inbox__body'>{input.body}</p>

      {input.replies.length > 0 ? (
        <div className='client-inbox__replies'>
          {input.replies.map((reply, idx) => (
            <div
              key={`${input.id}-reply-${idx}`}
              className={`client-inbox__reply client-inbox__reply--${
                reply.author_type === 'client' ? 'client' : 'employee'
              }`}
            >
              <div className='client-inbox__reply-head'>
                <span className='client-inbox__reply-author'>
                  {authorLabel(reply.author_type, t)}
                </span>
                <span className='client-inbox__reply-when'>
                  {formatWhen(reply.created_at, i18n.language)}
                </span>
              </div>
              <p className='client-inbox__reply-body'>{reply.body}</p>
            </div>
          ))}
        </div>
      ) : null}

      {resolved ? (
        <div className='client-inbox__resolved-note'>
          {t('ClientInboxResolvedNote', 'This thread is resolved.')}
        </div>
      ) : (
        <div className='client-inbox__actions'>
          <textarea
            className='client-inbox__reply-input'
            placeholder={t(
              'ClientInboxReplyPlaceholder',
              'Write a reply to the client…'
            )}
            value={draft}
            maxLength={REPLY_MAX}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className='client-inbox__action-row'>
            <span className='client-inbox__hint'>
              {t(
                'ClientInboxReplyHint',
                'Replying emails the client and marks this answered.'
              )}
            </span>
            <div className='client-inbox__action-btns'>
              <MainSquareButton
                type='button'
                value={t('ClientInboxResolve', 'Resolve')}
                buttonType='light'
                normal
                disabled={busy}
                onClick={() => onResolve(input.id)}
              />
              <MainSquareButton
                type='button'
                value={t('ClientInboxSendReply', 'Send reply')}
                buttonType='success'
                normal
                loading={busy}
                disabled={busy || draft.trim().length === 0}
                onClick={submitReply}
              />
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

const ClientInboxPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Read inside the fetch and the actions through a ref, so a language switch
  // never refetches the inbox.
  const tRef = useRef(t);
  tRef.current = t;

  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  const activeShopName = useMemo(
    () =>
      allShops.find((sh: any) => sh?.slug === activeShopSlug)?.name ||
      activeShopSlug,
    [allShops, activeShopSlug]
  );

  const [inputs, setInputs] = useState<ClientInput[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('open');
  // Which thread currently has an in-flight reply/resolve (keyed by input id).
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});

  const setBusy = (id: string, busy: boolean) =>
    setRowBusy((prev) => ({ ...prev, [id]: busy }));

  const loadInputs = useCallback(
    async (slug: string, statusFilter: StatusFilter) => {
      if (!slug) {
        setInputs([]);
        return;
      }
      setLoading(true);
      try {
        const rows = await listClientInputs({
          shopSlug: slug,
          status: statusFilter === 'all' ? undefined : statusFilter,
        });
        setInputs(rows);
      } catch (err: any) {
        // Never swallow this: rendering "empty inbox" on a 500 is a lie.
        const msg =
          err?.status === 403
            ? tRef.current(
                'AgentConfigNoBrandAccess',
                'You do not have access to that brand.'
              )
            : err?.status === 404
            ? tRef.current(
                'ClientInboxBrandNotFound',
                'That brand could not be found.'
              )
            : err?.message ||
              tRef.current(
                'ClientInboxLoadFailed',
                'Failed to load the client inbox.'
              );
        dispatch(setMessage({ message: msg, messageType: 'error' }));
        setInputs([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (INBOX_ENABLED) loadInputs(activeShopSlug, status);
  }, [activeShopSlug, status, loadInputs]);

  // Merge an updated thread back into the list. If the current filter no longer
  // matches its status, drop it from view (the count/filter stays honest).
  const applyUpdated = useCallback(
    (updated: ClientInput) => {
      setInputs((prev) => {
        if (!prev) return prev;
        const stillMatches = status === 'all' || updated.status === status;
        if (!stillMatches) {
          return prev.filter((it) => it.id !== updated.id);
        }
        return prev.map((it) => (it.id === updated.id ? updated : it));
      });
    },
    [status]
  );

  const handleReply = useCallback(
    async (id: string, body: string) => {
      setBusy(id, true);
      try {
        const updated = await replyToClientInput(id, body);
        applyUpdated(updated);
        dispatch(
          setMessage({
            message: tRef.current(
              'ClientInboxReplySent',
              'Reply sent — the client has been emailed.'
            ),
            messageType: 'success',
          })
        );
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? tRef.current(
                'AgentConfigNoBrandAccess',
                'You do not have access to that brand.'
              )
            : err?.status === 404
            ? tRef.current(
                'ClientInboxThreadGone',
                'That thread no longer exists.'
              )
            : err?.message ||
              tRef.current(
                'ClientInboxReplyFailed',
                'Failed to send the reply.'
              );
        dispatch(setMessage({ message: msg, messageType: 'error' }));
      } finally {
        setBusy(id, false);
      }
    },
    [applyUpdated, dispatch]
  );

  const handleResolve = useCallback(
    async (id: string) => {
      setBusy(id, true);
      try {
        const updated = await resolveClientInput(id);
        applyUpdated(updated);
        dispatch(
          setMessage({
            message: tRef.current(
              'ClientInboxThreadResolved',
              'Thread resolved.'
            ),
            messageType: 'success',
          })
        );
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? tRef.current(
                'AgentConfigNoBrandAccess',
                'You do not have access to that brand.'
              )
            : err?.status === 404
            ? tRef.current(
                'ClientInboxThreadGone',
                'That thread no longer exists.'
              )
            : err?.message ||
              tRef.current(
                'ClientInboxResolveFailed',
                'Failed to resolve the thread.'
              );
        dispatch(setMessage({ message: msg, messageType: 'error' }));
      } finally {
        setBusy(id, false);
      }
    },
    [applyUpdated, dispatch]
  );

  return (
    <SubPageInnerContentHolder
      title={t('ClientReports', 'Reporting')}
      backBtnLink={HOME_ROUTE}
    >
      <ClientReportsNav />

      <div className='client-inbox'>
        <div className='report-list__brand-field'>
          <label className='report-list__label'>
            {t('ClientReportsActiveBrand', 'Active brand')}
          </label>
          <div className='client-reports__active-brand'>
            <span className='client-reports__active-brand-name'>
              {activeShopName || '—'}
            </span>
            <span className='client-reports__active-brand-hint'>
              {t(
                'ClientReportsSwitchBrandHint',
                'Switch brand from the button in the bottom-left.'
              )}
            </span>
          </div>
        </div>

        <p className='client-inbox__intro'>
          {t(
            'ClientInboxIntro',
            "Questions and next-month requests your clients raise from their report portal land here. Reply to email the client back (the thread moves to Answered), or resolve it once it's handled."
          )}
        </p>

        {INBOX_ENABLED ? (
          <div
            className='client-reports__status-tabs'
            role='tablist'
            aria-label={t(
              'ClientInboxFilterAria',
              'Filter client inputs by status'
            )}
          >
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                type='button'
                role='tab'
                aria-selected={status === tab}
                className={`client-reports__status-tab${
                  status === tab ? ' client-reports__status-tab--active' : ''
                }`}
                onClick={() => setStatus(tab)}
              >
                {tab === 'all'
                  ? t('ClientInboxFilterAll', 'All')
                  : clientInputStatusLabel(tab, t)}
              </button>
            ))}
          </div>
        ) : null}

        {!INBOX_ENABLED ? (
          <div className='client-reports__empty'>
            {t(
              'ClientInboxUnavailable',
              'The client inbox becomes available here once the report portal is enabled for this workspace.'
            )}
          </div>
        ) : !activeShopSlug ? (
          <div className='client-reports__empty'>
            {t(
              'AgentsNoActiveBrand',
              'No active brand. Pick one from the button in the bottom-left.'
            )}
          </div>
        ) : loading || inputs === null ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner
              customLoadingMsg={t(
                'ClientInboxLoading',
                'Loading client inbox…'
              )}
            />
          </div>
        ) : inputs.length === 0 ? (
          <div className='client-reports__empty'>
            {t(`ClientInboxEmpty_${status}`, EMPTY_LABEL[status])}
          </div>
        ) : (
          <div className='client-inbox__list'>
            {inputs.map((input) => (
              <ThreadCard
                key={input.id}
                input={input}
                busy={!!rowBusy[input.id]}
                onReply={handleReply}
                onResolve={handleResolve}
              />
            ))}
          </div>
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default ClientInboxPage;
